import React from "react";
import { educationData } from "../config/personalInfo";

export default function Education() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            <span className="border-b-4 border-blue-500 pb-2">Education</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
            My academic journey and achievements
          </p>
        </div>

        <div className="space-y-8 md:space-y-12">
          {educationData.degrees.map((degree, index) => (
            <div key={index} className="group">
              {/* Header Card */}
              <div
                className={`relative bg-gradient-to-r ${
                  index === 0
                    ? "from-blue-600 to-blue-400"
                    : "from-gray-800 to-gray-600"
                } rounded-2xl p-6 sm:p-8 text-white shadow-lg transform transition-all duration-300 group-hover:scale-[1.02] group-hover:shadow-xl`}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-bold mb-2">
                      {degree.title}
                    </h2>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-blue-100">
                      <a
                        href={degree.institutionUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center hover:text-white transition-colors duration-200"
                      >
                        <span>{degree.institution}</span>
                        <svg
                          className="w-4 h-4 ml-1 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                      <span className="text-sm font-medium px-3 py-1 bg-white/10 rounded-full">
                        {degree.period}
                      </span>
                    </div>
                  </div>
                  {degree.tags && (
                    <div className="hidden md:block">
                      <div className="flex gap-2">
                        {degree.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-3 py-1 bg-white/10 rounded-full text-sm font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Content Cards Grid */}
              <div className="mt-6 grid md:grid-cols-2 gap-6">
                {/* Coursework Card */}
                <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                      <svg
                        className="w-5 h-5 mr-2 text-blue-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                        />
                      </svg>
                      Key Coursework
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {degree.coursework.map((course, courseIndex) => (
                        <div
                          key={courseIndex}
                          className="flex items-center space-x-2 p-2 rounded-lg bg-gray-50 hover:bg-blue-50 transition-colors duration-300"
                        >
                          <span className="flex-shrink-0 w-1.5 h-1.5 bg-blue-500 rounded-full" />
                          <span className="text-gray-700 text-sm">
                            {course}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Projects Card */}
                <div className="space-y-6">
                  {degree.projects.map((project, projectIndex) => (
                    <div
                      key={projectIndex}
                      className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden"
                    >
                      <div className="p-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                          <svg
                            className="w-5 h-5 mr-2 text-blue-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                          </svg>
                          {project.title}
                        </h3>
                        {project.description && (
                          <p className="text-gray-600 mb-4">
                            {project.description}
                          </p>
                        )}
                        <ul className="space-y-3">
                          {project.points.map((point, pointIndex) => (
                            <li
                              key={pointIndex}
                              className="flex items-start space-x-3 text-sm text-gray-700"
                            >
                              <span className="flex-shrink-0 w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5" />
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
